import { useState, useEffect, useRef } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Trophy, Loader2, Building2, Search, ChevronDown, X, Plus } from 'lucide-react';

export default function MarkAsHiredModal({
  candidate,
  onClose,
  onConfirm,
  loading = false
}) {
  const [hiredCompany, setHiredCompany] = useState('');
  const [hiredDate, setHiredDate] = useState('');
  const [hiredNotes, setHiredNotes] = useState('');
  // Phase 5 ("Record the money"), 4 Sep 2026: the one field this modal never
  // asked for. Without it, POST /hr-pipeline/mark-hired/{share_id} has no
  // salary to compute a fee from, so no placements document gets written.
  const [signedCtc, setSignedCtc] = useState('');

  // Company search/dropdown state
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('ats_token');
      const response = await fetch(`${API_URL}/api/companies?limit=500`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Failed to load companies:', error);
    } finally {
      setCompaniesLoading(false);
    }
  };

  const filteredCompanies = companies.filter(c =>
    c.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectCompany = (company) => {
    setHiredCompany(company.name);
    setSelectedCompanyId(company.id);
    setSearchQuery('');
    setShowDropdown(false);
  };

  const handleCustomCompany = () => {
    if (searchQuery.trim()) {
      setHiredCompany(searchQuery.trim());
      setSelectedCompanyId(null);
      setShowDropdown(false);
    }
  };

  const clearSelection = () => {
    setHiredCompany('');
    setSelectedCompanyId(null);
    setSearchQuery('');
  };

  const handleConfirm = () => {
    if (!hiredCompany.trim() || !hiredDate) return;

    const ctcNumber = signedCtc.trim() ? Number(signedCtc) : null;

    onConfirm({
      hired_company: hiredCompany.trim(),
      hired_company_id: selectedCompanyId,
      hired_at: hiredDate,
      hired_notes: hiredNotes.trim() || null,
      // Annual CTC in rupees, e.g. 1200000 for 12 LPA. Optional so an older
      // caller of this modal never breaks - but without it no fee gets
      // recorded for this placement.
      signed_annual_ctc: ctcNumber && ctcNumber > 0 ? ctcNumber : null
    });
  };

  if (!candidate) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-green-100 dark:bg-green-900/50 rounded-full flex items-center justify-center">
            <Trophy className="w-5 h-5 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            Mark as Hired
          </h3>
        </div>

        <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
          <strong>Candidate:</strong> {candidate.candidate_name}<br/>
          <strong>Position:</strong> {candidate.job_title || 'N/A'}<br/>
          <strong>Current Status:</strong> {candidate.current_status?.replace('_', ' ')}
        </p>

        <div className="space-y-4">
          {/* Company Dropdown/Search */}
          <div ref={dropdownRef}>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
              Hired at Company <span className="text-red-500">*</span>
            </label>

            {hiredCompany ? (
              // Selected company display
              <div className="flex items-center gap-2 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg">
                <Building2 className="w-4 h-4 text-green-600 dark:text-green-400" />
                <span className="flex-1 text-sm font-medium text-green-800 dark:text-green-200">{hiredCompany}</span>
                <button
                  onClick={clearSelection}
                  className="p-1 hover:bg-green-100 dark:hover:bg-green-800 rounded"
                >
                  <X className="w-4 h-4 text-green-600 dark:text-green-400" />
                </button>
              </div>
            ) : (
              // Search input
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    ref={inputRef}
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setShowDropdown(true);
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Search or type company name..."
                    className="pl-10 pr-10"
                    data-testid="company-search-input"
                  />
                  <ChevronDown className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 transition-transform ${showDropdown ? 'rotate-180' : ''}`} />
                </div>

                {/* Dropdown */}
                {showDropdown && (
                  <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border dark:border-slate-700 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {companiesLoading ? (
                      <div className="flex items-center justify-center py-4">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    ) : (
                      <>
                        {/* Custom entry option */}
                        {searchQuery.trim() && !filteredCompanies.some(c => c.name.toLowerCase() === searchQuery.toLowerCase()) && (
                          <button
                            onClick={handleCustomCompany}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-blue-50 dark:hover:bg-blue-900/30 border-b dark:border-slate-700"
                          >
                            <Plus className="w-4 h-4 text-blue-600" />
                            <span className="text-sm text-blue-600 dark:text-blue-400">
                              Use "<strong>{searchQuery}</strong>" as custom company
                            </span>
                          </button>
                        )}

                        {/* Company list */}
                        {filteredCompanies.length > 0 ? (
                          filteredCompanies.slice(0, 50).map((company) => (
                            <button
                              key={company.id}
                              onClick={() => handleSelectCompany(company)}
                              className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 dark:hover:bg-slate-700 border-b dark:border-slate-700 last:border-b-0"
                              data-testid={`company-option-${company.id}`}
                            >
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate">{company.name}</p>
                                {company.company_code && (
                                  <p className="text-xs text-slate-500 dark:text-slate-400">{company.company_code}</p>
                                )}
                              </div>
                            </button>
                          ))
                        ) : (
                          <div className="px-3 py-4 text-center text-sm text-slate-500">
                            {searchQuery ? 'No companies found' : 'Start typing to search...'}
                          </div>
                        )}

                        {filteredCompanies.length > 50 && (
                          <div className="px-3 py-2 text-center text-xs text-slate-500 bg-slate-50 dark:bg-slate-900">
                            Showing 50 of {filteredCompanies.length} results. Type to narrow down.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
              Hiring Date <span className="text-red-500">*</span>
            </label>
            <Input
              type="date"
              value={hiredDate}
              onChange={(e) => setHiredDate(e.target.value)}
              data-testid="hired-date-input"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
              Signed Annual CTC (Rs)
            </label>
            <Input
              type="number"
              min="0"
              step="1000"
              value={signedCtc}
              onChange={(e) => setSignedCtc(e.target.value)}
              placeholder="e.g. 1200000 for 12 LPA"
              data-testid="signed-ctc-input"
            />
            <p className="text-[11px] text-slate-400 mt-1">
              The candidate's actual accepted annual CTC. Used to compute the DCS fee at the
              client's agreed rate, or the service-fee rate card if none is on file. Leave blank
              only if genuinely unknown - no fee gets recorded for this placement without it.
            </p>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5 block">
              Notes (optional)
            </label>
            <textarea
              className="w-full border dark:border-slate-700 rounded-lg p-3 text-sm bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100"
              rows={2}
              placeholder="Any additional notes..."
              value={hiredNotes}
              onChange={(e) => setHiredNotes(e.target.value)}
            />
          </div>
        </div>

        <p className="text-xs text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/30 p-3 rounded-lg mt-4">
          🎉 This candidate will be marked as successfully placed and will appear in the Hired Candidates page.
        </p>

        <div className="flex gap-2 justify-end mt-4">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={loading || !hiredCompany.trim() || !hiredDate}
            className="bg-green-600 hover:bg-green-700"
            data-testid="confirm-hired-btn"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Trophy className="w-4 h-4 mr-2" />
            )}
            Confirm Hired
          </Button>
        </div>
      </div>
    </div>
  );
}
